import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaLinkedin,
  FaTwitter,
  FaWhatsapp,
  FaYoutube,
  FaClock,
  FaCalendarAlt,
} from "react-icons/fa";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
};

const formatDuration = (dur) => {
  if (!dur) return null;
  const [h, m] = dur.split(":").map(Number);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h} hour${h > 1 ? "s" : ""}`;
  return `${m} min`;
};

const isUpcoming = (d) => {
  if (!d) return true;
  return new Date(d) >= new Date();
};

// Render Quill Delta JSON as plain text
const extractDetailsText = (detailsJson) => {
  if (!detailsJson) return "";
  try {
    const parsed = JSON.parse(detailsJson);
    return parsed.ops
      .map((op) => op.insert)
      .join("")
      .replace(/\n+/g, " ")
      .trim();
  } catch {
    return "";
  }
};

// ─── Component ───────────────────────────────────────────────────────────────
const ChurchServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/services/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch services: ${response.status}`);
        const data = await response.json();
        const results = Array.isArray(data) ? data : (data.results ?? []);
        setServices(results);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const upcoming = services.filter((s) => {
    if (s.runs_every_day || !s.start_day) return true;
    return isUpcoming(s.start_day);
  });

  const past = services.filter((s) => {
    if (s.runs_every_day || !s.start_day) return false;
    return !isUpcoming(s.start_day);
  });

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
      <div style={{ width: 44, height: 44, border: "4px solid #e8f4fd", borderTopColor: "#0b1c6b", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ textAlign: "center", padding: "80px 20px" }}>
      <div style={{ fontSize: "3.5rem", marginBottom: 16 }}>⚠️</div>
      <h4 style={{ fontSize: "1.1rem", color: "#0b1c6b", marginBottom: 8 }}>Could not load services</h4>
      <p style={{ color: "#6b7280" }}>{error}</p>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');

        :root {
          --navy:   #0b1c6b;
          --sky:    #159EEC;
          --lsky:   #e8f4fd;
          --dark:   #111827;
          --mid:    #6b7280;
          --light:  #f9fafb;
          --white:  #ffffff;
          --border: #e5e7eb;
          --sh-sm:  0 2px 8px  rgba(11,28,107,0.07);
          --sh-md:  0 8px 30px rgba(11,28,107,0.12);
          --sh-lg:  0 20px 60px rgba(11,28,107,0.15);
        }

        .csp * { box-sizing: border-box; margin: 0; padding: 0; }
        .csp { font-family: 'Lexend', sans-serif; color: var(--dark); background: var(--white); overflow-x: hidden; }

        .csp-hero {
          position: relative;
          height: 420px;
          background: url('/images/church2.jpg') center/cover no-repeat;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
        }
        .csp-hero::before {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(11,28,107,0.93), rgba(21,158,236,0.82));
        }
        .csp-hero-content { position: relative; text-align: center; color: #fff; padding: 0 20px; }
        .csp-badge {
          display: inline-block;
          background: rgba(255,255,255,0.14);
          border: 1px solid rgba(255,255,255,0.28);
          font-size: 0.72rem; font-weight: 600;
          letter-spacing: 2px; text-transform: uppercase;
          padding: 6px 18px; border-radius: 50px; margin-bottom: 18px; color: #fff;
        }
        .csp-hero-content h1 { font-size: clamp(2.2rem,5vw,3.6rem); font-weight: 800; line-height: 1.1; margin-bottom: 14px; }
        .csp-hero-content p { font-size: 1rem; color: rgba(255,255,255,0.82); font-weight: 300; max-width: 500px; margin: 0 auto; line-height: 1.7; }

        .csp-wrap { max-width: 1200px; margin: 0 auto; padding: 80px 24px; }
        .csp-label { font-size: 0.68rem; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: var(--sky); margin-bottom: 8px; }
        .csp-heading { font-size: clamp(1.7rem,3vw,2.4rem); font-weight: 700; color: var(--navy); line-height: 1.2; margin-bottom: 10px; }
        .csp-divider { width: 50px; height: 4px; background: linear-gradient(90deg, var(--navy), var(--sky)); border-radius: 4px; margin: 14px 0 40px; }

        .csp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 28px; }

        .csp-card { background: var(--white); border: 1px solid var(--border); border-radius: 18px; overflow: hidden; box-shadow: var(--sh-sm); transition: transform 0.3s, box-shadow 0.3s; cursor: pointer; display: flex; flex-direction: column; }
        .csp-card:hover { transform: translateY(-6px); box-shadow: var(--sh-lg); }

        .csp-card-accent { height: 6px; background: linear-gradient(90deg, var(--navy), var(--sky)); }
        .csp-card-accent.past { background: linear-gradient(90deg, #9ca3af, #d1d5db); }
        .csp-card-accent.recurring { background: linear-gradient(90deg, #10b981, #34d399); }

        .csp-card-body { padding: 26px 24px; flex: 1; }
        .csp-card-date { display: inline-flex; align-items: center; gap: 6px; background: var(--lsky); color: var(--sky); font-size: 0.75rem; font-weight: 600; padding: 5px 12px; border-radius: 50px; margin-bottom: 14px; }
        .csp-card-date.past { background: #f3f4f6; color: var(--mid); }
        .csp-card-date.recurring { background: #d1fae5; color: #10b981; }
        .csp-card-title { font-size: 1.05rem; font-weight: 700; color: var(--navy); margin-bottom: 10px; line-height: 1.35; }
        .csp-card-desc { font-size: 0.87rem; color: var(--mid); line-height: 1.7; font-weight: 300; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; margin-bottom: 20px; }
        .csp-card-meta { display: flex; gap: 16px; flex-wrap: wrap; border-top: 1px solid var(--border); padding-top: 16px; }
        .csp-meta-item { display: flex; align-items: center; gap: 6px; font-size: 0.8rem; color: var(--mid); font-weight: 400; }
        .csp-meta-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--sky); flex-shrink: 0; }
        .csp-meta-dot.past { background: #d1d5db; }
        .csp-meta-dot.recurring { background: #10b981; }

        .csp-card-footer { padding: 0 24px 22px; }
        .csp-read-btn { width: 100%; background: var(--lsky); color: var(--navy); border: none; padding: 11px; border-radius: 10px; font-family: 'Lexend', sans-serif; font-weight: 600; font-size: 0.85rem; cursor: pointer; transition: background 0.2s, color 0.2s; }
        .csp-read-btn:hover { background: var(--navy); color: #fff; }
        .csp-read-btn.past { background: #f3f4f6; color: var(--mid); }
        .csp-read-btn.past:hover { background: #6b7280; color: #fff; }
        .csp-read-btn.recurring { background: #d1fae5; color: #10b981; }
        .csp-read-btn.recurring:hover { background: #10b981; color: #fff; }

        .csp-past-bg { background: var(--light); }

        .csp-modal-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; padding: 20px; animation: cspFadeIn 0.25s ease; }
        @keyframes cspFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .csp-modal { background: var(--white); border-radius: 20px; max-width: 560px; width: 100%; max-height: 90vh; overflow-y: auto; box-shadow: 0 30px 80px rgba(0,0,0,0.25); animation: cspSlideUp 0.3s ease; }
        @keyframes cspSlideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

        .csp-modal-top { height: 8px; background: linear-gradient(90deg, var(--navy), var(--sky)); border-radius: 20px 20px 0 0; }
        .csp-modal-top.recurring { background: linear-gradient(90deg, #10b981, #34d399); }
        .csp-modal-body { padding: 32px; }
        .csp-modal-date-tag { display: inline-flex; align-items: center; gap: 6px; background: var(--lsky); color: var(--sky); font-size: 0.75rem; font-weight: 600; padding: 5px 14px; border-radius: 50px; margin-bottom: 18px; }
        .csp-modal-date-tag.recurring { background: #d1fae5; color: #10b981; }
        .csp-modal-title { font-size: 1.5rem; font-weight: 700; color: var(--navy); margin-bottom: 20px; line-height: 1.3; }
        .csp-modal-info { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 24px; }
        .csp-modal-info-item { background: var(--light); border-radius: 12px; padding: 16px; }
        .csp-modal-info-item span:first-child { display: block; font-size: 0.68rem; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--sky); margin-bottom: 6px; }
        .csp-modal-info-item span:last-child { font-size: 0.92rem; font-weight: 600; color: var(--navy); }
        .csp-modal-details { font-size: 0.93rem; color: var(--mid); line-height: 1.8; font-weight: 300; border-top: 1px solid var(--border); padding-top: 20px; margin-bottom: 28px; }
        .csp-modal-actions { display: flex; gap: 12px; justify-content: flex-end; }
        .csp-modal-close { background: var(--light); color: var(--mid); border: none; padding: 12px 24px; border-radius: 10px; font-family: 'Lexend', sans-serif; font-weight: 600; font-size: 0.88rem; cursor: pointer; transition: background 0.2s; }
        .csp-modal-close:hover { background: var(--border); }

        .csp-social { background: linear-gradient(135deg, #0b1c6b, #159EEC); color: white; text-align: center; padding: 60px 24px; }
        .csp-social h3 { font-size: 1.6rem; font-weight: 700; margin-bottom: 8px; }
        .csp-social p { color: rgba(255,255,255,0.75); font-size: 0.93rem; font-weight: 300; margin-bottom: 32px; }
        .csp-social-links { display: flex; justify-content: center; gap: 14px; flex-wrap: wrap; }
        .csp-social-link { display: flex; align-items: center; gap: 8px; padding: 11px 20px; border-radius: 50px; border: 1.5px solid rgba(255,255,255,0.3); color: #fff; text-decoration: none; font-size: 0.86rem; font-weight: 600; background: rgba(255,255,255,0.1); transition: all 0.25s; }
        .csp-social-link:hover { background: rgba(255,255,255,0.25); transform: translateY(-3px); }

        .csp-empty { text-align: center; padding: 60px 20px; color: var(--mid); }
        .csp-empty-icon { font-size: 3rem; margin-bottom: 14px; }

        @media (max-width: 600px) {
          .csp-grid { grid-template-columns: 1fr; }
          .csp-modal-info { grid-template-columns: 1fr; }
          .csp-hero { height: 320px; }
        }
      `}</style>

      <div className="csp">

        {/* HERO */}
        <section className="csp-hero">
          <div className="csp-hero-content">
            <span className="csp-badge">Gospel City Church International</span>
            <h1>Church Services</h1>
            <p>God alone is enough. He doesn't need extra help.</p>
          </div>
        </section>

        {/* UPCOMING */}
        <div className="csp-wrap">
          <p className="csp-label">On the calendar</p>
          <h2 className="csp-heading">Upcoming Services</h2>
          <div className="csp-divider"></div>

          {upcoming.length === 0 ? (
            <div className="csp-empty">
              <div className="csp-empty-icon"><FaCalendarAlt /></div>
              <p>No upcoming services at the moment. Check back soon!</p>
            </div>
          ) : (
            <div className="csp-grid">
              {upcoming.map((s) => (
                <ServiceCard key={s.id} service={s} past={false} onClick={() => setSelected(s)} />
              ))}
            </div>
          )}
        </div>

        {/* PAST */}
        {past.length > 0 && (
          <div className="csp-past-bg">
            <div className="csp-wrap">
              <p className="csp-label">What we've had</p>
              <h2 className="csp-heading" style={{ color: "#6b7280" }}>Past Services</h2>
              <div className="csp-divider" style={{ background: "linear-gradient(90deg, #9ca3af, #d1d5db)" }}></div>
              <div className="csp-grid">
                {past.map((s) => (
                  <ServiceCard key={s.id} service={s} past={true} onClick={() => setSelected(s)} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MODAL */}
        {selected && <ServiceModal service={selected} onClose={() => setSelected(null)} />}

        {/* CONTACT CTA */}
        <div style={{ textAlign: "center", padding: "50px 24px", background: "#f9fafb", borderTop: "1px solid #e5e7eb" }}>
          <p style={{ color: "#6b7280", fontSize: "0.9rem", marginBottom: 16, fontFamily: "'Lexend', sans-serif" }}>
            Have questions about our services?
          </p>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <button
              style={{ background: "#0b1c6b", color: "#fff", border: "none", padding: "13px 36px", borderRadius: "50px", fontFamily: "'Lexend', sans-serif", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer", boxShadow: "0 6px 20px rgba(11,28,107,0.25)", transition: "transform 0.2s" }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
            >
              Contact Us →
            </button>
          </Link>
        </div>

        {/* SOCIAL */}
        <section className="csp-social">
          <h3>Follow Us</h3>
          <p>Stay updated on upcoming services and events through our socials.</p>
          <div className="csp-social-links">
            <a href="https://www.linkedin.com/in/gospel-city-church-international-650a562a0/" target="_blank" rel="noopener noreferrer" className="csp-social-link"><FaLinkedin /> LinkedIn</a>
            <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
            <i className="bi bi-chat-dots fs-4"></i>
            </a>
            <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="csp-social-link"><FaTwitter /> Twitter / X</a>
            <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="csp-social-link"><FaWhatsapp /> WhatsApp</a>
            <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="csp-social-link"><FaYoutube /> YouTube</a>
          </div>
        </section>

      </div>
    </>
  );
};

// ─── ServiceCard ─────────────────────────────────────────────────────────────
const ServiceCard = ({ service: s, past, onClick }) => {
  const isRecurring = s.runs_every_day || (s.day_of_week && !s.is_single_day);
  const details = extractDetailsText(s.detailsJson);

  let dateDisplay = "";
  if (s.is_single_day && s.start_day)    dateDisplay = formatDate(s.start_day);
  else if (s.runs_every_day)             dateDisplay = "Every Day";
  else if (s.day_of_week_display)        dateDisplay = `Every ${s.day_of_week_display}`;
  else                                   dateDisplay = "Ongoing";

  return (
    <div className="csp-card" onClick={onClick}>
      <div className={`csp-card-accent${past ? " past" : ""}${isRecurring ? " recurring" : ""}`}></div>
      <div className="csp-card-body">
        <div className={`csp-card-date${past ? " past" : ""}${isRecurring ? " recurring" : ""}`}>
          <FaCalendarAlt /> {dateDisplay}
        </div>
        <h3 className="csp-card-title">{s.title}</h3>
        {details && <p className="csp-card-desc">{details}</p>}
        <div className="csp-card-meta">
          <div className="csp-meta-item">
            <span className={`csp-meta-dot${past ? " past" : ""}${isRecurring ? " recurring" : ""}`}></span>
            {formatTime(s.start_time)}{s.end_time && ` – ${formatTime(s.end_time)}`}
          </div>
          {s.lasts_for && (
            <div className="csp-meta-item">
              <span className={`csp-meta-dot${past ? " past" : ""}${isRecurring ? " recurring" : ""}`}></span>
              {formatDuration(s.lasts_for)}
            </div>
          )}
        </div>
      </div>
      <div className="csp-card-footer">
        <button className={`csp-read-btn${past ? " past" : ""}${isRecurring ? " recurring" : ""}`}>
          View Details →
        </button>
      </div>
    </div>
  );
};

// ─── ServiceModal ─────────────────────────────────────────────────────────────
const ServiceModal = ({ service: s, onClose }) => {
  const isRecurring = s.runs_every_day || (s.day_of_week && !s.is_single_day);
  const details = extractDetailsText(s.detailsJson);

  let dateDisplay = "";
  if (s.is_single_day && s.start_day)    dateDisplay = formatDate(s.start_day);
  else if (s.runs_every_day)             dateDisplay = "Every Day";
  else if (s.day_of_week_display)        dateDisplay = `Every ${s.day_of_week_display}`;
  else                                   dateDisplay = "Ongoing";

  return (
    <div className="csp-modal-overlay" onClick={onClose}>
      <div className="csp-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`csp-modal-top${isRecurring ? " recurring" : ""}`}></div>
        <div className="csp-modal-body">
          <div className={`csp-modal-date-tag${isRecurring ? " recurring" : ""}`}>
            <FaCalendarAlt /> {dateDisplay}
          </div>
          <h2 className="csp-modal-title">{s.title}</h2>

          <div className="csp-modal-info">
            <div className="csp-modal-info-item">
              <span>Start Time</span>
              <span><FaClock /> {formatTime(s.start_time)}</span>
            </div>
            {s.end_time && (
              <div className="csp-modal-info-item">
                <span>End Time</span>
                <span><FaClock /> {formatTime(s.end_time)}</span>
              </div>
            )}
            {s.lasts_for && (
              <div className="csp-modal-info-item">
                <span>Duration</span>
                <span><FaClock /> {formatDuration(s.lasts_for)}</span>
              </div>
            )}
            <div className="csp-modal-info-item">
              <span>Type</span>
              <span><FaCalendarAlt /> {isRecurring ? "Recurring" : "Single Day Event"}</span>
            </div>
            {s.start_day && s.end_day && (
              <>
                <div className="csp-modal-info-item">
                  <span>Starts</span>
                  <span>{formatDate(s.start_day)}</span>
                </div>
                <div className="csp-modal-info-item">
                  <span>Ends</span>
                  <span>{formatDate(s.end_day)}</span>
                </div>
              </>
            )}
          </div>

          {details && <p className="csp-modal-details">{details}</p>}

          <div className="csp-modal-actions">
            <button className="csp-modal-close" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChurchServices;